import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Loader2, Search, User, X } from 'lucide-react';
import {
  useState,
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type SyntheticEvent,
} from 'react';
import { useSelector } from 'react-redux';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { getUsersSimplePaginated, type UserSimpleResponse } from '@/apis/users-paginated';
import { PiiMask } from '@/components/PiiMask';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useDebounce } from '@/hooks/useDebounce';
import { usePermissions } from '@/hooks/usePermissions';
import { NavigationService } from '@/lib/navigation-actions';
import {
  NavigationItem,
  filterNavigationIndex,
  navigationIndex,
  searchNavigationItems,
} from '@/lib/navigation-index';
import { generateSearchVariations, safeHighlightMatch } from '@/lib/search-utils';
import { RootState } from '@/redux/store';
import { canListCognitoUsers } from '@/utils/roles';
import { getUserProfileId } from '@/utils/userHelpers';
import {
  getUserSimpleDisplayName,
  matchesUserSearch,
  USER_SEARCH_FIELDS,
  type UserSearchField,
} from '@/utils/userSearch';
import { VoiceSearchButton } from './VoiceSearchButton';

const SEARCH_DEBOUNCE_MS = 250;
const CLIENT_RESULT_LIMIT = 8;

const scopes = ['All', 'Clients', 'Pages', 'Client tabs', 'Other tabs'] as const;
type SearchScope = (typeof scopes)[number];

const fieldPlaceholders: Record<UserSearchField, string> = {
  all: 'Search by name, email, Client ID, or phone…',
  name: 'Search clients by first or last name…',
  email: 'Search clients by email…',
  client_id: 'Search by Client ID…',
  phone: 'Search by phone number…',
};

const clientCommandValue = (user: UserSimpleResponse) =>
  `client:${getUserProfileId(user) || user.uuid}`;

const clientLabel = (user: UserSimpleResponse) => getUserSimpleDisplayName(user);

const clientRoutes = [
  '/client/:service/:clientId/visit/:visitId',
  '/client/discovery/:clientId',
  '/client/:clientId/*',
  '/member/:page/:clientId/*',
];

const isSearchScope = (value: unknown): value is SearchScope =>
  typeof value === 'string' && (scopes as readonly string[]).includes(value);

export function UniversalCommandPalette() {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scope, setScope] = useState<SearchScope>('All');
  const [searchField, setSearchField] = useState<UserSearchField>('all');
  const [selectedValue, setSelectedValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const suppressItemSelect = useRef(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userRole = useSelector((state: RootState) => state.user.role);
  const canSearchClients = canListCognitoUsers(userRole);
  const visibleScopes = scopes.filter((category) => category !== 'Clients' || canSearchClients);
  const currentClientId = clientRoutes
    .map((path) => matchPath(path, pathname)?.params.clientId)
    .find(Boolean);
  const { hasPermission, isLoading: isPermissionsLoading } = usePermissions();
  const navigationService = useMemo(() => new NavigationService(navigate), [navigate]);

  const trimmedQuery = searchQuery.trim();
  const debouncedQuery = useDebounce(trimmedQuery, SEARCH_DEBOUNCE_MS);
  const showClients =
    canSearchClients &&
    (scope === 'All' || scope === 'Clients') &&
    trimmedQuery.length >= 2 &&
    debouncedQuery === trimmedQuery;
  const clientSearchTerm = trimmedQuery.length >= 2 ? debouncedQuery : '';

  const {
    data: clientData,
    isFetching: clientsLoading,
    isError: clientsError,
  } = useQuery({
    queryKey: ['universal-search-clients', clientSearchTerm, searchField, userRole],
    queryFn: ({ signal }) =>
      getUsersSimplePaginated(
        {
          search: clientSearchTerm,
          search_by: searchField,
          role: 'CLIENT',
          page: 1,
          page_size: CLIENT_RESULT_LIMIT,
        },
        undefined,
        { signal }
      ),
    enabled: open && showClients && Boolean(clientSearchTerm),
    staleTime: 30_000,
  });

  const clients = useMemo(() => {
    if (!showClients || clientsError) return [];
    return (clientData?.results ?? [])
      .filter((user) => getUserProfileId(user))
      .filter((user) => matchesUserSearch(user, debouncedQuery, searchField))
      .slice(0, CLIENT_RESULT_LIMIT);
  }, [showClients, clientsError, clientData?.results, debouncedQuery, searchField]);

  const searchResults = useMemo(() => {
    if (!userRole || isPermissionsLoading) return [];

    const results = searchQuery.trim()
      ? generateSearchVariations(searchQuery).flatMap((variation) =>
          searchNavigationItems(variation, hasPermission, currentClientId, userRole)
        )
      : filterNavigationIndex(navigationIndex, hasPermission, userRole);

    return Array.from(new Map(results.map((item) => [item.id, item])).values()).filter(
      (item) => !item.metadata?.clientRequired || currentClientId
    );
  }, [searchQuery, userRole, currentClientId, hasPermission, isPermissionsLoading]);

  const groupedResults = useMemo(() => {
    const groups: Record<string, NavigationItem[]> = {};
    for (const item of searchResults) {
      const category = item.metadata?.clientRequired
        ? 'Client tabs'
        : item.category === 'page'
          ? 'Pages'
          : 'Other tabs';
      if (scope !== 'All' && scope !== category) continue;
      (groups[category] ??= []).push(item);
    }
    return Object.entries(groups);
  }, [searchResults, scope]);
  const showNavigation = scope !== 'Clients';
  const destinationCount = showNavigation
    ? groupedResults.reduce((count, [, items]) => count + items.length, 0)
    : 0;
  const awaitingClientSearch =
    canSearchClients &&
    (scope === 'All' || scope === 'Clients') &&
    trimmedQuery.length >= 2 &&
    debouncedQuery !== trimmedQuery;
  const clientsPending =
    awaitingClientSearch ||
    (showClients && clientsLoading && clients.length === 0 && !clientsError);
  const resultCount = destinationCount + clients.length;

  const openClient = (user: UserSimpleResponse, destination: 'details' | 'prep') => {
    const userId = getUserProfileId(user);
    if (!userId) return;
    window.open(`/client/${userId}/${destination}`, '_blank', 'noopener,noreferrer');
  };

  const openClientFromButton = (
    event: SyntheticEvent,
    user: UserSimpleResponse,
    destination: 'details' | 'prep'
  ) => {
    event.preventDefault();
    event.stopPropagation();
    suppressItemSelect.current = true;
    openClient(user, destination);
  };

  useEffect(() => {
    const down = (event: globalThis.KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const modified = event.metaKey || event.ctrlKey;
      if (!modified) return;
      if (key === 'j') {
        event.preventDefault();
        event.stopPropagation();
        setOpen((value) => !value);
        return;
      }
      if (key === 'p') {
        event.preventDefault();
        event.stopPropagation();
        setScope(canSearchClients ? 'Clients' : 'All');
        setOpen(true);
      }
    };
    const handleCustomOpen = (event: Event) => {
      const requested = (event as CustomEvent<{ scope?: SearchScope }>).detail?.scope;
      if (isSearchScope(requested) && (requested !== 'Clients' || canSearchClients)) {
        setScope(requested);
      }
      setOpen(true);
    };
    document.addEventListener('keydown', down, true);
    window.addEventListener('openUniversalSearch', handleCustomOpen);
    return () => {
      document.removeEventListener('keydown', down, true);
      window.removeEventListener('openUniversalSearch', handleCustomOpen);
    };
  }, [canSearchClients]);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setScope('All');
      setSearchField('all');
    }
  }, [open]);

  useEffect(() => {
    const firstClient = clients[0];
    setSelectedValue(
      firstClient ? clientCommandValue(firstClient) : (groupedResults[0]?.[1][0]?.id ?? '')
    );
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [groupedResults, clients, open]);

  const handleSelectItem = (item: NavigationItem) => {
    if (item.metadata?.clientRequired && !currentClientId) return;
    navigationService.navigateToItem(item, currentClientId);
    setOpen(false);
  };

  const handleInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || !(event.metaKey || event.ctrlKey)) return;
    const client = clients.find((user) => clientCommandValue(user) === selectedValue);
    if (!client) return;
    event.preventDefault();
    event.stopPropagation();
    openClient(client, 'prep');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="flex w-[calc(100%-2rem)] max-w-xl flex-col gap-0 overflow-hidden p-0 sm:p-0">
        <div className="shrink-0 px-4 pb-3 pt-4 pr-14">
          <DialogTitle className="text-base">Go to…</DialogTitle>
          <DialogDescription className="mt-1 text-xs">
            Narrow by Name, Email, Client ID, or Phone. ⌘P jumps to clients.
          </DialogDescription>
        </div>
        <Command
          shouldFilter={false}
          loop
          value={selectedValue}
          onValueChange={setSelectedValue}
          className="min-h-0 rounded-none"
        >
          <div className="flex shrink-0 items-center border-y px-1 [&_[cmdk-input-wrapper]]:min-w-0 [&_[cmdk-input-wrapper]]:flex-1 [&_[cmdk-input-wrapper]]:border-0">
            <CommandInput
              ref={inputRef}
              value={searchQuery}
              onValueChange={setSearchQuery}
              onKeyDown={handleInputKeyDown}
              placeholder={
                canSearchClients && (scope === 'All' || scope === 'Clients')
                  ? fieldPlaceholders[searchField]
                  : 'Search pages and tabs…'
              }
              aria-label="Search clients, pages, and tabs"
              className="h-12 text-base sm:text-sm"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Clear search"
                onClick={() => {
                  setSearchQuery('');
                  inputRef.current?.focus();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <VoiceSearchButton onTranscript={setSearchQuery} size="sm" className="mr-2" />
          </div>
          {canSearchClients && (scope === 'All' || scope === 'Clients') && (
            <div
              aria-label="Client search fields"
              className="flex shrink-0 gap-1 overflow-x-auto border-b px-2 py-1.5"
            >
              {USER_SEARCH_FIELDS.map((field) => (
                <Button
                  key={field.id}
                  variant={searchField === field.id ? 'secondary' : 'ghost'}
                  size="sm"
                  aria-pressed={searchField === field.id}
                  data-testid={`search_field_${field.id}`}
                  onClick={() => {
                    setSearchField(field.id);
                    if (field.id !== 'all') setScope('Clients');
                    inputRef.current?.focus();
                  }}
                  className="h-7 shrink-0 px-2.5 text-xs"
                >
                  {field.label}
                </Button>
              ))}
            </div>
          )}
          <div
            aria-label="Search categories"
            className="flex shrink-0 gap-1 overflow-x-auto border-b p-2"
          >
            {visibleScopes.map((category) => (
              <Button
                key={category}
                variant={scope === category ? 'secondary' : 'ghost'}
                size="sm"
                aria-pressed={scope === category}
                disabled={category === 'Client tabs' && !currentClientId}
                title={
                  category === 'Client tabs' && !currentClientId
                    ? 'Open a client to search their tabs'
                    : undefined
                }
                onClick={() => {
                  setScope(category);
                  inputRef.current?.focus();
                }}
                className="shrink-0 px-3 text-xs"
              >
                {category}
              </Button>
            ))}
          </div>
          <CommandList
            ref={listRef}
            aria-label="Destinations"
            className="min-h-0 max-h-[min(420px,45dvh)] overscroll-contain p-1"
          >
            {scope === 'Clients' && trimmedQuery.length < 2 ? (
              <div role="status" className="px-6 py-10 text-center text-sm">
                <User className="mx-auto mb-3 h-5 w-5 text-muted-foreground" />
                <p className="font-medium">Search clients</p>
                <p className="mt-1 text-muted-foreground">
                  {searchField === 'name'
                    ? 'Try a first name, last name, or both.'
                    : searchField === 'email'
                      ? 'Try the email address or the part before @.'
                      : searchField === 'client_id'
                        ? 'Try a Client ID or the start of one, like PMX25.'
                        : searchField === 'phone'
                          ? 'Try the phone number without country code.'
                          : 'Type at least 2 characters to find a client by name, email, Client ID, or phone.'}
                </p>
              </div>
            ) : resultCount === 0 && !clientsPending && !clientsError ? (
              <div role="status" className="px-6 py-10 text-center text-sm">
                <Search className="mx-auto mb-3 h-5 w-5 text-muted-foreground" />
                <p className="font-medium">
                  {trimmedQuery.length >= 2
                    ? 'No matching pages or clients'
                    : 'No matching destinations'}
                </p>
                <p className="mt-1 text-muted-foreground">
                  {trimmedQuery.length >= 2
                    ? 'Switch to Name if email matches are crowding the list, or pick Email / Client ID / Phone.'
                    : 'Try a shorter search, a Client ID, or another category.'}
                </p>
              </div>
            ) : (
              <>
                {(clients.length > 0 || clientsPending || clientsError) && (
                  <CommandGroup heading="Clients">
                    {clientsError ? (
                      <div role="alert" className="px-3 py-3 text-sm text-muted-foreground">
                        Could not search clients.
                      </div>
                    ) : clientsPending ? (
                      <div
                        role="status"
                        className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground"
                      >
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Searching clients…
                      </div>
                    ) : (
                      clients.map((user) => {
                        const email = user.Profile?.email || user.email;
                        const phone = user.Profile?.phone_number;
                        const clientId = user.Profile?.client_id;
                        const program = user.Profile?.program_names?.[0];
                        const afterEmail = [
                          searchField === 'phone' ? phone : null,
                          clientId,
                          program,
                        ].filter(Boolean);
                        return (
                          <CommandItem
                            key={clientCommandValue(user)}
                            value={clientCommandValue(user)}
                            onSelect={() => {
                              if (suppressItemSelect.current) {
                                suppressItemSelect.current = false;
                                return;
                              }
                              openClient(user, 'details');
                            }}
                            className="group min-h-12 cursor-pointer gap-3 rounded-md px-3 py-2"
                          >
                            <User
                              className="h-4 w-4 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">
                                <PiiMask kind="name" value={clientLabel(user)} />
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {email ? <PiiMask kind="email" value={email} /> : null}
                                {email && afterEmail.length > 0 ? ' · ' : null}
                                {searchField === 'phone' && phone ? (
                                  <>
                                    <PiiMask kind="phone" value={phone} />
                                    {clientId || program ? ' · ' : null}
                                  </>
                                ) : null}
                                {[clientId, program].filter(Boolean).join(' · ') || null}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 rounded-full px-2.5 text-[11px]"
                                aria-label={`Prep for ${clientLabel(user)}`}
                                onPointerDown={(event) => {
                                  event.stopPropagation();
                                  suppressItemSelect.current = true;
                                }}
                                onClick={(event) => openClientFromButton(event, user, 'prep')}
                              >
                                Prep
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 rounded-full px-2.5 text-[11px]"
                                aria-label={`Client Details for ${clientLabel(user)}`}
                                onPointerDown={(event) => {
                                  event.stopPropagation();
                                  suppressItemSelect.current = true;
                                }}
                                onClick={(event) => openClientFromButton(event, user, 'details')}
                              >
                                Client Details
                              </Button>
                            </div>
                          </CommandItem>
                        );
                      })
                    )}
                  </CommandGroup>
                )}
                {showNavigation &&
                  groupedResults.map(([category, items]) => (
                    <CommandGroup
                      key={category}
                      heading={category === 'Client tabs' ? 'Current client' : category}
                    >
                      {items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <CommandItem
                            key={item.id}
                            value={item.id}
                            onSelect={() => handleSelectItem(item)}
                            className="group min-h-12 cursor-pointer gap-3 rounded-md px-3 py-2"
                          >
                            <Icon
                              className="h-4 w-4 shrink-0 text-muted-foreground"
                              aria-hidden="true"
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">
                                {safeHighlightMatch(item.title, searchQuery)}
                              </p>
                              <p className="truncate text-xs text-muted-foreground">
                                {item.metadata?.parentPage || item.description}
                              </p>
                            </div>
                            <ArrowUpRight
                              className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 group-data-[selected=true]:opacity-100"
                              aria-hidden="true"
                            />
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ))}
              </>
            )}
          </CommandList>
          <div className="flex shrink-0 items-center justify-between border-t px-4 py-2.5 text-xs text-muted-foreground">
            <span aria-live="polite">
              {resultCount} {resultCount === 1 ? 'result' : 'results'}
            </span>
            <span className="hidden sm:inline">↑ ↓ Navigate · Enter details · Esc Close</span>
            <span className="sm:hidden">Tap a result to open</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
